import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

// Types
export interface OverviewMetrics {
  totalUsers: number;
  activeUsers: number;
  newUsersToday: number;
  totalWorkspaces: number;
  activeWorkspaces: number;
  totalConversations: number;
  totalMessages: number;
  totalTokens: number;
  totalCost: number;
  totalDatasources: number;
  totalEmbeddings: number;
  storageUsedMB: number;
}

export interface UserMetrics {
  id: string;
  email: string;
  name: string;
  lastActive: string | null;
  conversationCount: number;
  messageCount: number;
  tokensUsed: number;
  costGenerated: number;
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
}

export interface WorkspaceMetrics {
  id: string;
  name: string;
  owner: string;
  memberCount: number;
  conversationCount: number;
  messageCount: number;
  datasourceCount: number;
  tokensUsed: number;
  costGenerated: number;
  storageUsedMB: number;
  createdAt: string;
}

export interface ChartDataPoint {
  date: string;
  value: number;
  label?: string;
}

export interface LiveEvent {
  id: string;
  type: 'user_login' | 'user_logout' | 'conversation_started' | 'message_sent' | 'datasource_created' | 'error';
  userId?: string;
  userName?: string;
  workspaceId?: string;
  workspaceName?: string;
  message: string;
  timestamp: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface AdminMonitoringState {
  // Overview
  overview: OverviewMetrics | null;
  overviewHistory: ChartDataPoint[];

  // Users
  users: {
    data: UserMetrics[];
    pagination: Pagination;
  };
  selectedUser: UserMetrics | null;

  // Workspaces
  workspaces: {
    data: WorkspaceMetrics[];
    pagination: Pagination;
  };
  selectedWorkspace: WorkspaceMetrics | null;

  // Messages
  messages: {
    throughput: number;
    history: ChartDataPoint[];
    byModel: { model: string; count: number }[];
  };

  // Files
  files: {
    totalCount: number;
    totalSizeMB: number;
    byType: { type: string; count: number; sizeMB: number }[];
    byStatus: { status: string; count: number }[];
    history: ChartDataPoint[];
  };

  // Live
  live: {
    activeUsersCount: number;
    activeConversations: number;
    messagesPerMinute: number;
    events: LiveEvent[];
  };

  // Status
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  lastUpdated: number | null;
}

const initialPagination: Pagination = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
};

const initialState: AdminMonitoringState = {
  overview: null,
  overviewHistory: [],
  users: {
    data: [],
    pagination: initialPagination,
  },
  selectedUser: null,
  workspaces: {
    data: [],
    pagination: initialPagination,
  },
  selectedWorkspace: null,
  messages: {
    throughput: 0,
    history: [],
    byModel: [],
  },
  files: {
    totalCount: 0,
    totalSizeMB: 0,
    byType: [],
    byStatus: [],
    history: [],
  },
  live: {
    activeUsersCount: 0,
    activeConversations: 0,
    messagesPerMinute: 0,
    events: [],
  },
  status: 'idle',
  error: null,
  lastUpdated: null,
};

// Async thunks
export const fetchOverviewMetrics = createAsyncThunk(
  'adminMonitoring/fetchOverview',
  async (params: { startDate?: string; endDate?: string } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const response = await fetch(`/api/admin/monitoring/overview?${queryParams}`);
    if (!response.ok) throw new Error('Failed to fetch overview metrics');
    return response.json();
  }
);

export const fetchUserMetrics = createAsyncThunk(
  'adminMonitoring/fetchUsers',
  async (params: { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', String(params.page));
    if (params.limit) queryParams.append('limit', String(params.limit));
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await fetch(`/api/admin/monitoring/users/overview?${queryParams}`);
    if (!response.ok) throw new Error('Failed to fetch user metrics');
    return response.json();
  }
);

export const fetchWorkspaceMetrics = createAsyncThunk(
  'adminMonitoring/fetchWorkspaces',
  async (params: { page?: number; limit?: number; sortBy?: string; sortOrder?: 'asc' | 'desc' } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', String(params.page));
    if (params.limit) queryParams.append('limit', String(params.limit));
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const response = await fetch(`/api/admin/monitoring/workspaces/overview?${queryParams}`);
    if (!response.ok) throw new Error('Failed to fetch workspace metrics');
    return response.json();
  }
);

export const fetchMessageMetrics = createAsyncThunk(
  'adminMonitoring/fetchMessages',
  async (params: { startDate?: string; endDate?: string; granularity?: 'day' | 'week' | 'month' } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);
    if (params.granularity) queryParams.append('granularity', params.granularity);

    const response = await fetch(`/api/admin/monitoring/messages/overview?${queryParams}`);
    if (!response.ok) throw new Error('Failed to fetch message metrics');
    return response.json();
  }
);

export const fetchFileMetrics = createAsyncThunk(
  'adminMonitoring/fetchFiles',
  async (params: { startDate?: string; endDate?: string } = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('startDate', params.startDate);
    if (params.endDate) queryParams.append('endDate', params.endDate);

    const response = await fetch(`/api/admin/monitoring/files/overview?${queryParams}`);
    if (!response.ok) throw new Error('Failed to fetch file metrics');
    return response.json();
  }
);

export const fetchLiveStatus = createAsyncThunk(
  'adminMonitoring/fetchLiveStatus',
  async () => {
    const response = await fetch('/api/admin/monitoring/live/status');
    if (!response.ok) throw new Error('Failed to fetch live status');
    return response.json();
  }
);

// Slice
const adminMonitoringSlice = createSlice({
  name: 'adminMonitoring',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedUser: (state, action: PayloadAction<UserMetrics | null>) => {
      state.selectedUser = action.payload;
    },
    setSelectedWorkspace: (state, action: PayloadAction<WorkspaceMetrics | null>) => {
      state.selectedWorkspace = action.payload;
    },
    addLiveEvent: (state, action: PayloadAction<LiveEvent>) => {
      state.live.events.unshift(action.payload);
      // Keep only last 100 events
      if (state.live.events.length > 100) {
        state.live.events = state.live.events.slice(0, 100);
      }
    },
    updateLiveMetrics: (state, action: PayloadAction<Partial<AdminMonitoringState['live']>>) => {
      state.live = { ...state.live, ...action.payload };
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    // Overview
    builder.addCase(fetchOverviewMetrics.pending, (state) => {
      state.status = 'loading';
      state.error = null;
    });
    builder.addCase(fetchOverviewMetrics.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.overview = action.payload.data.overview;
      state.overviewHistory = action.payload.data.history || [];
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchOverviewMetrics.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch overview';
    });

    // Users
    builder.addCase(fetchUserMetrics.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(fetchUserMetrics.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.users.data = action.payload.data.users;
      state.users.pagination = action.payload.data.pagination;
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchUserMetrics.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch users';
    });

    // Workspaces
    builder.addCase(fetchWorkspaceMetrics.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(fetchWorkspaceMetrics.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.workspaces.data = action.payload.data.workspaces;
      state.workspaces.pagination = action.payload.data.pagination;
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchWorkspaceMetrics.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch workspaces';
    });

    // Messages
    builder.addCase(fetchMessageMetrics.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(fetchMessageMetrics.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.messages = action.payload.data;
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchMessageMetrics.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch messages';
    });

    // Files
    builder.addCase(fetchFileMetrics.pending, (state) => {
      state.status = 'loading';
    });
    builder.addCase(fetchFileMetrics.fulfilled, (state, action) => {
      state.status = 'succeeded';
      state.files = action.payload.data;
      state.lastUpdated = Date.now();
    });
    builder.addCase(fetchFileMetrics.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.error.message || 'Failed to fetch files';
    });

    // Live status
    builder.addCase(fetchLiveStatus.fulfilled, (state, action) => {
      state.live = { ...state.live, ...action.payload.data };
      state.lastUpdated = Date.now();
    });
  },
});

export const {
  clearError,
  setSelectedUser,
  setSelectedWorkspace,
  addLiveEvent,
  updateLiveMetrics,
  resetState,
} = adminMonitoringSlice.actions;

export default adminMonitoringSlice.reducer;
